import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TenantConfigService {

  constructor(private http: HttpClient) {}

  private tenantConfigMap: { [tenantId: number]: any } = {};

  private loaded = false;

  private readonly cityDisplayOrder: { [key: string]: number } = {

    pune: 1,

    nashik: 2,

    bangalore: 3

  };

  private readonly serviceIconMap = {

    "Badminton": "../../assets/img/badminton2.svg",
    "Tennis": "../../assets/img/Tennis.svg",
    "Pickleball": "../../assets/img/Pickleball.svg",
    "Padel": "../../assets/img/paddle.svg",
    "Squash": "../../assets/img/Squash.svg",
    "Table Tennis": "../../assets/img/TableTennis.svg",
    "Cricket": "../../assets/img/cricket.svg",
    "Cricket Nets": "../../assets/img/cricket.svg",
    "Football": "../../assets/img/football2.svg",
    "Basketball": "../../assets/img/basketball.svg",
    "Volleyball": "../../assets/img/Volleyball.svg",
    "Swimming": "../../assets/img/Swimming.svg",
    "Gym & Fitness": "../../assets/img/gym.svg",
    "Gymnastics": "../../assets/img/gymnastics.svg",
    "Yoga": "../../assets/img/Yoga.svg",
    "Martial Arts": "../../assets/img/MartialArts.svg",
    "Skating": "../../assets/img/Skating.svg",
    "Chess": "../../assets/img/chess.svg",
    "Athletics": "../../assets/img/Athletics.svg",
    "Cycling": "../../assets/img/cycling.svg",
    "Archery": "../../assets/img/Archery.svg",
    "Golf": "../../assets/img/golf.svg",
    "Climbing": "../../assets/img/climbing.svg",
    "Dance": "../../assets/img/Dance.svg",
    "Classical Dance": "../../assets/img/Classical_Dance.svg",
    "Pool": "../../assets/img/Pool.svg",
    "Sport": "../../assets/img/Others.svg",
    "Coaching": "../../assets/img/coach.svg"

  };

  //-------------------------------------------------------
  // Load tenant-config.json
  //-------------------------------------------------------

  load(): Observable<boolean> {

      if (this.loaded) {
          return of(true);
      }

     return this.http
       .get<any>('assets/tenant-config.json')
       .pipe(

         tap(config => {

           this.tenantConfigMap = {};

           (config.tenants || []).forEach(t => {

             this.tenantConfigMap[t.tenantId] = t;

           });

           this.loaded = true;

         }),

         map(() => true),

         catchError(error => {

           console.error('Unable to load tenant-config.json', error);

           this.tenantConfigMap = {};

           this.loaded = true;

           return of(true);

         })

       );
  }

  //-------------------------------------------------------
  // Tenant Config
  //-------------------------------------------------------

  getTenantConfig(tenantId: number): any {

    return this.tenantConfigMap[tenantId];

  }

  //-------------------------------------------------------
  // Services
  //-------------------------------------------------------

  getTenantServices(tenantId: number): string[] {

    return this.tenantConfigMap[tenantId]?.services || [];

  }

  //-------------------------------------------------------
  // Icons
  //-------------------------------------------------------

  getTenantIcons(tenantId: number): string[] {

    const services =

      this.getTenantServices(tenantId);

    return services

      .map(service => this.serviceIconMap[service])

      .filter(icon => !!icon);

  }

  //-------------------------------------------------------
  // Sort
  //-------------------------------------------------------

  sortTenants(tenants: any[]): any[] {

    tenants.forEach((tenant, index) => {

      tenant.originalIndex = index;

      const config =

        this.tenantConfigMap[tenant.id];

      const seq =

        Number(config?.sequence);

      tenant.sequence =

        Number.isFinite(seq)

          ? seq

          : Number.MAX_SAFE_INTEGER;

    });

    tenants.sort((a, b) => {

      const cityA =

        (a.address?.city || '')

          .trim()

          .toLowerCase();

      const cityB =

        (b.address?.city || '')

          .trim()

          .toLowerCase();

      const cityPriorityA =

        this.cityDisplayOrder[cityA] ?? 999;

      const cityPriorityB =

        this.cityDisplayOrder[cityB] ?? 999;

      //------------------------------------
      // City Priority
      //------------------------------------

      if (cityPriorityA !== cityPriorityB) {

        return cityPriorityA - cityPriorityB;

      }

      //------------------------------------
      // Remaining Cities
      //------------------------------------

      if (cityPriorityA === 999) {

        const compare =

          cityA.localeCompare(cityB);

        if (compare !== 0) {

          return compare;

        }

      }

      //------------------------------------
      // Sequence
      //------------------------------------

      if (a.sequence !== b.sequence) {

        return a.sequence - b.sequence;

      }

      //------------------------------------
      // Original API Order
      //------------------------------------

      return a.originalIndex - b.originalIndex;

    });

    return tenants;

  }

}
